import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const PANEL_IMG = "/img/hero-family.jpg";
const REGULATORS = ["DIFC · DFSA", "FINMA", "MAS Singapore", "FCA London", "SEC"];

export default function Hero() {
  const { t } = useLanguage();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <section data-testid="hero-section" className="bg-paper pt-20 md:pt-24 pb-10 overflow-hidden">
      <div className="max-w-[1800px] mx-auto px-3 md:px-5 lg:px-6">
        {/* Subject panel */}
        <div className="relative z-10 rounded-[28px] md:rounded-[36px] overflow-hidden bg-brand">
          <img
            src={PANEL_IMG}
            alt="Three generations of a family, representing wealth that endures"
            className="absolute inset-0 w-full h-full object-cover mix-blend-luminosity opacity-95"
            style={{ objectPosition: "50% 28%" }}
          />
          <div className="absolute inset-0 pointer-events-none" style={{ background: "linear-gradient(90deg, rgba(8,44,39,.94) 0%, rgba(8,44,39,.62) 42%, rgba(8,44,39,.2) 72%, rgba(8,44,39,.35) 100%)" }} />
          <div className="absolute inset-x-0 bottom-0 h-1/2 pointer-events-none" style={{ background: "linear-gradient(180deg, rgba(8,44,39,0) 0%, rgba(8,44,39,.55) 100%)" }} />

          <div className="relative min-h-[620px] md:min-h-[820px] lg:min-h-[900px] flex flex-col justify-end p-7 md:p-14 lg:p-16">
            <div className="max-w-2xl">
              <h2 className="font-display font-semibold text-white text-4xl md:text-6xl lg:text-7xl leading-[1.02] tracking-tight max-w-[16ch]">
                {t.hero.title1} {t.hero.title2}
              </h2>
              <p className="mt-6 text-base md:text-lg text-white/85 leading-relaxed max-w-xl">{t.hero.sub}</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  data-testid="hero-cta-primary"
                  onClick={() => scrollTo("consultation")}
                  className="group inline-flex items-center gap-2 bg-white text-brand hover:bg-brand-50 transition-colors text-sm font-semibold pl-6 pr-2 py-2 rounded-full"
                >
                  {t.hero.ctaPrimary}
                  <span className="w-8 h-8 rounded-full bg-brand text-white grid place-items-center group-hover:translate-x-0.5 transition-transform">
                    <ArrowRight className="w-4 h-4" />
                  </span>
                </button>
                <button
                  data-testid="hero-cta-secondary"
                  onClick={() => scrollTo("services")}
                  className="border border-white/40 text-white hover:bg-white/10 transition-colors text-sm font-semibold px-6 py-2.5 rounded-full"
                >
                  {t.hero.ctaSecondary}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Trusted / regulated row */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-6 md:gap-12">
          <p className="text-sm text-ink/50 leading-snug shrink-0">
            Trusted across the<br className="hidden md:block" /> world's leading centres
          </p>
          <div className="flex flex-wrap items-center gap-x-9 gap-y-3">
            {REGULATORS.map((r) => (
              <span key={r} className="font-display font-semibold text-ink/70 text-base md:text-lg tracking-tight">{r}</span>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
